"use client";

import React, { useId, useState } from "react";

import type { StylistTag } from "@/lib/stylists";

import styles from "./styles.module.scss";

/**
 * Openings team member tags (booking-flow standard 2.2.0 § 12, presentation
 * 2.4.1).
 *
 * Business-authored, customer-facing notes on a stylist — e.g. the languages
 * they speak ("Korean · basic English", "Bring a photo of the style you
 * want"). Openings owns the tags and their text; this site only decides
 * where they appear. The text is shown as written, never rephrased.
 *
 * `MemberTagBadges` renders the labels as small tags, sized like the
 * Openings booking page's pills but with 4px corners (Oct 21, owner
 * request). Since Oct 21 this site shows them only inside
 * booking: under a "Useful info" heading in the location page's mini
 * profile, and under the stylist on the review step and the confirmation.
 * A tag with a description is a button; pressing it reveals
 * the description as one line of secondary text under the pills, and
 * pressing again hides it. A tag without a description is a plain pill.
 * (Until Oct 20 this site showed each described tag in a "Before your visit"
 * notes block, as Openings did before 2.4.1.)
 */

/**
 * Accessible name of the pill list on the review step and the confirmation
 * (Openings `booking.review.memberNotes` / `booking.confirmation.memberNotes`).
 */
export const MEMBER_TAG_NOTES_LABEL = "Before your visit";

function noteOf(tag: StylistTag): string | null {
  const text = tag.description?.trim();
  return text ? text : null;
}

export function MemberTagBadges({
  tags,
  label = "About this stylist",
  className,
}: {
  tags?: readonly StylistTag[] | null;
  /** Accessible name of the list. */
  label?: string;
  className?: string;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const noteId = useId();
  if (!tags?.length) return null;
  const openTag = tags.find((tag) => tag.id === openId) ?? null;
  const openNote = openTag ? noteOf(openTag) : null;
  return (
    <div className={className}>
      <ul className={styles.badges} aria-label={label}>
        {tags.map((tag) => {
          const note = noteOf(tag);
          const isOpen = note !== null && tag.id === openId;
          return (
            <li key={tag.id}>
              {note ? (
                <button
                  type="button"
                  className={styles.pill}
                  aria-expanded={isOpen}
                  aria-controls={noteId}
                  onClick={() =>
                    setOpenId((current) => (current === tag.id ? null : tag.id))
                  }
                >
                  {tag.label}
                </button>
              ) : (
                <span className={styles.pill}>{tag.label}</span>
              )}
            </li>
          );
        })}
      </ul>
      {openNote ? (
        <p id={noteId} className={styles.note}>
          {openNote}
        </p>
      ) : null}
    </div>
  );
}
